import { CalendarOutlined, ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	DatePicker,
	Form,
	Input,
	Row,
	Select,
	Space,
	Tag,
	TimePicker,
	Typography,
} from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useMe } from "@/modules/auth/hooks/useAuth";
import { useStaffList } from "@/modules/staff/hooks/useStaff";
import { PageHeader } from "@/shared/components/PageHeader";
import { type UrlParamPatch, useUrlTableState } from "@/shared/hooks/useUrlState";
import { DailyAttendanceTable } from "../components/DailyAttendanceTable";
import { useCreateDailyAttendance } from "../hooks/useDailyAttendance";
import type { AttendancePeriod, CreateDailyAttendanceRequest } from "../types";

const { Text } = Typography;

interface FormValues {
	staffId: string;
	date: dayjs.Dayjs;
	period: AttendancePeriod;
	organizationId?: string;
	time: dayjs.Dayjs;
	overrideReason: string;
}

export function CreateAttendancePage() {
	const [form] = Form.useForm<FormValues>();
	const { data: meUser } = useMe();
	const organizations = meUser?.organizations ?? [];
	const {
		page: tablePage,
		limit: tableLimit,
		setPage: setTablePage,
		getFilter,
		setFilters,
	} = useUrlTableState();
	// Table date filter lives in the URL as YYYY-MM-DD; defaults to today.
	const tableDate = getFilter("date") ?? dayjs().format("YYYY-MM-DD");

	const hasMultipleOrgs = organizations.length > 1;

	// Auto-pick primary org, fallback to first org
	const defaultOrg = useMemo(
		() => organizations.find((o) => o.isPrimary) ?? organizations[0] ?? null,
		[organizations]
	);

	const selectedOrgId = getFilter("org") ?? null;

	const activeOrg = useMemo(() => {
		if (hasMultipleOrgs) {
			return organizations.find((o) => o.id === selectedOrgId) ?? null;
		}
		return defaultOrg;
	}, [hasMultipleOrgs, selectedOrgId, organizations, defaultOrg]);

	const { data: staffData, isLoading: staffLoading } = useStaffList({
		organizationId: activeOrg?.id ?? "",
		page: 1,
		limit: 100,
		isActive: true,
	});

	const { mutate: createAttendance, isPending } = useCreateDailyAttendance();

	const staffOptions =
		staffData?.data?.items?.map((s) => ({
			value: s.id,
			label: s.fullname,
		})) ?? [];

	const handleFinish = (values: FormValues) => {
		if (!activeOrg) {
			return;
		}

		const baseDate = values.date;
		const payload: CreateDailyAttendanceRequest = {
			date: baseDate.format("YYYY-MM-DD"),
			staffId: values.staffId,
			organizationId: activeOrg.id,
			period: values.period,
			time: baseDate
				.hour(values.time.hour())
				.minute(values.time.minute())
				.second(0)
				.millisecond(0)
				.toISOString(),
			overrideReason: values.overrideReason,
		};

		createAttendance(payload, {
			onSuccess: () => {
				// Point the table at the created record's date and reset page (plus
				// the org drill-down for multi-org users) in a single history entry.
				const patch: UrlParamPatch = { date: values.date.format("YYYY-MM-DD") };
				if (hasMultipleOrgs) {
					patch.org = null;
				}
				setFilters(patch);
				const resetFields: (keyof FormValues)[] = ["staffId", "period", "time", "overrideReason"];
				if (hasMultipleOrgs) {
					resetFields.push("organizationId");
				}
				form.resetFields(resetFields);
			},
		});
	};

	return (
		<div>
			<PageHeader title="Davomat qo'shish" />

			<Card
				bordered={false}
				style={{ borderRadius: 12, marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
			>
				<div className="mb-4 flex items-center gap-2 flex-wrap">
					<Text type="secondary">Yangi kunlik davomat yozuvini qo'lda yarating.</Text>
					{!hasMultipleOrgs && activeOrg && (
						<Tag color="blue" style={{ margin: 0 }}>
							{activeOrg.name}
						</Tag>
					)}
				</div>

				<Form
					form={form}
					layout="vertical"
					onFinish={handleFinish}
					initialValues={{ date: dayjs(), period: "MORNING" as AttendancePeriod }}
				>
					<Row gutter={[12, 0]} align="bottom" wrap={false} style={{ flexWrap: "nowrap" }}>
						{/* Organization selector — only shown when user belongs to multiple orgs */}
						{hasMultipleOrgs && (
							<Col flex="0 0 220px">
								<Form.Item
									name="organizationId"
									label="Tashkilot"
									rules={[{ required: true, message: "Tashkilotni tanlang" }]}
									style={{ marginBottom: 0 }}
								>
									<Select
										placeholder="Tashkilotni tanlang"
										allowClear
										showSearch
										optionFilterProp="label"
										options={organizations.map((o) => ({ value: o.id, label: o.name }))}
										onChange={(val: string) => {
											// Org change + table page reset in one history entry.
											setFilters({ org: val ?? null });
											form.resetFields(["staffId"]);
										}}
									/>
								</Form.Item>
							</Col>
						)}

						{/* Staff */}
						<Col flex="0 0 260px">
							<Form.Item
								name="staffId"
								label="Xodim"
								rules={[{ required: true, message: "Xodimni tanlang" }]}
								style={{ marginBottom: 0 }}
							>
								<Select
									placeholder={
										hasMultipleOrgs && !activeOrg ? "Avval tashkilotni tanlang" : "Xodimni tanlang"
									}
									allowClear
									showSearch
									optionFilterProp="label"
									loading={staffLoading}
									options={staffOptions}
									disabled={hasMultipleOrgs && !activeOrg}
									notFoundContent={staffLoading ? "Yuklanmoqda..." : "Xodim topilmadi"}
								/>
							</Form.Item>
						</Col>

						{/* Date */}
						<Col flex="0 0 190px">
							<Form.Item
								name="date"
								label="Sana"
								rules={[{ required: true, message: "Sanani tanlang" }]}
								style={{ marginBottom: 0 }}
							>
								<DatePicker
									placeholder="Sana"
									format="DD.MM.YYYY"
									className="w-full"
									suffixIcon={<CalendarOutlined />}
								/>
							</Form.Item>
						</Col>

						{/* Period */}
						<Col flex="0 0 150px">
							<Form.Item
								name="period"
								label="Davr"
								rules={[{ required: true, message: "Davrni tanlang" }]}
								style={{ marginBottom: 0 }}
							>
								<Select
									options={[
										{ value: "MORNING", label: "Ertalab" },
										{ value: "NOON", label: "Kunduz" },
										{ value: "EVENING", label: "Kechqurun" },
									]}
								/>
							</Form.Item>
						</Col>

						{/* Time */}
						<Col flex="0 0 160px">
							<Form.Item
								name="time"
								label="Vaqt"
								rules={[{ required: true, message: "Vaqtni tanlang" }]}
								style={{ marginBottom: 0 }}
							>
								<TimePicker
									placeholder="HH:mm"
									format="HH:mm"
									className="w-full"
									suffixIcon={<ClockCircleOutlined />}
									minuteStep={5}
								/>
							</Form.Item>
						</Col>

						{/* Override Reason */}
						<Col flex="1 1 0" style={{ minWidth: 160 }}>
							<Form.Item
								name="overrideReason"
								label="Sabab"
								rules={[{ required: true, message: "Sabab ko'rsatilishi shart" }]}
								style={{ marginBottom: 0 }}
							>
								<Input placeholder="Sababni kiriting..." maxLength={500} />
							</Form.Item>
						</Col>

						{/* Submit */}
						<Col flex="0 0 150px">
							<Form.Item label=" " style={{ marginBottom: 0 }}>
								<Button
									type="primary"
									htmlType="submit"
									loading={isPending}
									disabled={hasMultipleOrgs ? !activeOrg : false}
									icon={<PlusOutlined />}
									block
								>
									Qo'shish
								</Button>
							</Form.Item>
						</Col>
					</Row>
				</Form>
			</Card>

			{/* Table of existing records */}
			{activeOrg && (
				<Card
					bordered={false}
					style={{ borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
					title={
						<Space>
							<CalendarOutlined />
							<span>Mavjud davomat yozuvlari</span>
							<Text type="secondary" style={{ fontWeight: 400 }}>
								— {activeOrg.name}
							</Text>
						</Space>
					}
				>
					<DailyAttendanceTable
						organizationId={activeOrg.id}
						organizationName={activeOrg.name}
						date={tableDate}
						status={null}
						page={tablePage}
						limit={tableLimit}
						onPageChange={setTablePage}
					/>
				</Card>
			)}
		</div>
	);
}
